import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { Toaster } from "./components/ui/sonner";
import LoadingOverlay from "./components/ui/LoadingOverlay";
import { useSnapshot } from "valtio";
import { authStore } from "./stores/authStore";
import { uiStore } from "./stores/uiStore";
import { QuestionBankProvider } from "./contexts/QuestionBankContext";

// Auth Components
import { RedirectIfAuthenticated } from "./components/auth/RedirectIfAuthenticated";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import AuthSwitcher from "./pages/auth/AuthSwitcher";

// Layouts
import SeafarerDashboardLayout from "./pages/seafarer/Layout";
import AdminDashboardLayout from "./pages/admin/Layout";

// Pages
import NotFound from "./pages/NotFound";
import Unauthorized from "./pages/Unauthorized";

// Seafarer Pages
import Courses from "./pages/seafarer/Courses";
import CourseDetails from "./pages/seafarer/CourseDetails";
import Progress from "./pages/seafarer/Progress";
import Profile from "./pages/seafarer/Profile";
import Tickets from "./pages/seafarer/Tickets";
import QuizHistory from "./pages/seafarer/QuizHistory";
import AssessmentWindow from "./pages/seafarer/AssessmentWindow";

// Admin Pages
import AdminDashboard from "./pages/admin/dashboard";
import AdminHealth from "./pages/admin/health";
import AdminProfile from "./pages/admin/profile";
import ManageUsers from "./pages/admin/manage-users";
import AddUserPage from "./pages/admin/manage-users/add/AddUserPage";
import ViewUser from "./pages/admin/manage-users/view/[id]";
import EditUser from "./pages/admin/manage-users/edit/[id]";
import ManageCourses from "./pages/admin/manage-courses";
import CourseBuilder from "./pages/admin/course-builder";
import ManageTickets from "./pages/admin/manage-tickets";
import ManageNotifications from "./pages/admin/manage-notifications";
import SystemSettings from "./pages/admin/settings";
import Analysis from "./pages/admin/analysis";
import { SidebarProvider } from "./components/ui/sidebar";
import CourseMaterial from "./components/courses/CourseMaterial";
import SetupPasswordRedirect from "./components/auth/SetupPasswordRedirect";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { WebSocketDebugger } from "./components/ui/websocketdegugger";
import TestResult from "./pages/seafarer/TestResult";
import CertificateView from "./pages/seafarer/CertificateView";
import { getCurrentUser } from "./api/userApi";
import { permissionStore } from "./stores/permissionStore";
import AboutPage from "./components/About/page";
import FAQSection from "./components/FAQ/page";

// Errors reach React Query in three shapes: a raw AxiosError, the plain
// `{message, statusCode}` object our api/ modules reject with, or a bare Error
// re-thrown by a query fn. Only the first two carry a status.
const getErrorStatus = (error) =>
  error?.response?.status ?? error?.statusCode ?? undefined;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // How long data is considered fresh (1 hour)
      staleTime: 60 * 60 * 1000, // 1 hour in milliseconds

      // How long data stays in cache after component unmounts (1 hour)
      gcTime: 60 * 60 * 1000, // Updated from cacheTime in v5

      // Don't refetch on window focus for notifications
      refetchOnWindowFocus: false,

      // Don't refetch on reconnect for cached data
      refetchOnReconnect: "always",

      // Retry failed requests (useful for network issues)
      retry: (failureCount, error) => {
        const status = getErrorStatus(error);
        // 429s are already retried with Retry-After backoff by the axios
        // interceptor; retrying again here just adds load.
        if (status !== undefined && status >= 400 && status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },

      // How long to wait between retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,

      // How long to wait before retrying mutations
      retryDelay: 1000,
    },
  },
});

// Role-based route components
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useSnapshot(authStore);
  const location = useLocation();

  if (loading) {
    return <LoadingOverlay isLoading={loading} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Show loading state while checking auth

  // If user role is not admin or super_admin, show unauthorized
  if (user?.role !== "ADMIN" && user?.role !== "SUPER_ADMIN") {
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  // If authenticated and authorized, render the admin dashboard
  return <AdminDashboardLayout>{children}</AdminDashboardLayout>;
};

const SeafarerRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useSnapshot(authStore);
  const location = useLocation();

  if (loading) {
    return <LoadingOverlay isLoading={loading} />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (user?.role === "ADMIN" || user?.role === "SUPER_ADMIN") {
    return (
      <Navigate to="/admin/dashboard" state={{ from: location }} replace />
    );
  }

  // If user role is not seafarer, show unauthorized
  if (user?.role?.toLowerCase() !== "seafarer") {
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  // If authenticated and authorized, render the dashboard
  return <SeafarerDashboardLayout>{children}</SeafarerDashboardLayout>;
};

// Main App Component
const AppContent = () => {
  const location = useLocation();
  const { isAuthenticated } = useSnapshot(authStore);
  // Update breadcrumbs when route changes
  useEffect(() => {
    const pathname = location.pathname;
    const pathParts = pathname.split("/").filter(Boolean);

    const newBreadcrumbs = pathParts.map((part, index) => {
      const path = `/${pathParts.slice(0, index + 1).join("/")}`;
      const name = part
        .replace(/-/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase());
      return { name, path };
    });

    uiStore.setBreadcrumbs(newBreadcrumbs);
  }, [location]);

  useEffect(() => {
    if (isAuthenticated) {
      userDetailsFetching();
    }
  }, [isAuthenticated]);

  const userDetailsFetching = async () => {
    try {
      const response = await getCurrentUser();
      // Update permissions if user data is returned
      if (response?.data) {
        const userData = response.data;
        
        // Extract permissions from API response
        let userPermissions = userData.permissions;
        if (typeof userPermissions === 'string') {
          try {
            userPermissions = JSON.parse(userPermissions);
          } catch (e) {
            console.error('Failed to parse permissions:', e);
            userPermissions = null;
          }
        }
        
        if (userPermissions && typeof userPermissions === 'object') {
          permissionStore.setPermissions(userPermissions);
        } else if (userData.role) {
          // Fallback to role-based permissions
          permissionStore.setRole(userData.role);
        }
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col overflow-x-hidden">
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            <RedirectIfAuthenticated>
              <div className="mb-10">
                <AuthSwitcher />
                <AboutPage />
                <FAQSection />
              </div>
            </RedirectIfAuthenticated>
          }
        />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ForgotPassword />} />
        <Route path="/setup-password" element={<SetupPasswordRedirect />} />

        {/* Seafarer Routes */}
        <Route
          path="/dashboard"
          element={
            <SeafarerRoute>
              <Courses />
            </SeafarerRoute>
          }
        />
        <Route
          path="/dashboard/courses"
          element={
            <SeafarerRoute>
              <Navigate to="/dashboard" />
            </SeafarerRoute>
          }
        />
        <Route
          path="/dashboard/courses/:courseId"
          element={
            <SeafarerRoute>
              <CourseDetails />
            </SeafarerRoute>
          }
        />
        <Route
          path="/dashboard/courses/:courseId/:materialId"
          element={
            <SeafarerRoute>
              <CourseMaterial />
            </SeafarerRoute>
          }
        />
        <Route
          path="/dashboard/courses/:courseId/quiz-history"
          element={
            <SeafarerRoute>
              <QuizHistory />
            </SeafarerRoute>
          }
        />
        <Route
          path="/dashboard/courses/:courseId/test/:quizId"
          element={
            <SeafarerRoute>
              <AssessmentWindow />
            </SeafarerRoute>
          }
        />
        <Route
          path="/dashboard/courses/:courseId/assessment/result/:attemptId"
          element={
            <SeafarerRoute>
              <TestResult />
            </SeafarerRoute>
          }
        />
        <Route
          path="/dashboard/progress"
          element={
            <SeafarerRoute>
              <Progress />
            </SeafarerRoute>
          }
        />
        <Route
          path="/dashboard/certificate/:courseId"
          element={
            <SeafarerRoute>
              <CertificateView />
            </SeafarerRoute>
          }
        />
        <Route
          path="/dashboard/profile"
          element={
            <SeafarerRoute>
              <Profile />
            </SeafarerRoute>
          }
        />
        <Route
          path="/dashboard/tickets"
          element={
            <SeafarerRoute>
              <Tickets />
            </SeafarerRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/profile"
          element={
            <AdminRoute>
              <AdminProfile />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        {/* AdminRoute admits ADMIN as well; the page itself turns non-super
            admins away, since the API behind it is @RequireLevel(3). */}
        <Route
          path="/admin/health"
          element={
            <AdminRoute>
              <AdminHealth />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/manage-users"
          element={
            <AdminRoute>
              <Navigate to="/admin/users" replace />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/manage-courses"
          element={
            <AdminRoute>
              <ManageCourses />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/course-builder/:courseId?"
          element={
            <AdminRoute>
              <CourseBuilder />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/manage-tickets"
          element={
            <AdminRoute>
              <ManageTickets />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/manage-notifications"
          element={
            <AdminRoute>
              <ManageNotifications />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <AdminRoute>
              <SystemSettings />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/analysis"
          element={
            <AdminRoute>
              <Analysis />
            </AdminRoute>
          }
        />
        {/* Admin Users Routes */}
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <ManageUsers />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users/new"
          element={
            <AdminRoute>
              <AddUserPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users/view/:id"
          element={
            <AdminRoute>
              <ViewUser />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users/edit/:id"
          element={
            <AdminRoute>
              <EditUser />
            </AdminRoute>
          }
        />

        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      <Toaster position="top-right" richColors />
    </div>
  );
};

// Main App component
const App = () => {
  return (
    <div className="min-h-screen w-full bg-[#f6fafc]">
      <div className="mx-auto flex min-h-screen w-full max-w-[1920px] flex-col">
        <QueryClientProvider client={queryClient}>
          <QuestionBankProvider>
            <Router>
              <SidebarProvider>
                <AppContent />
              </SidebarProvider>
            </Router>
          </QuestionBankProvider>

          {import.meta.env.VITE_APP_ENV === "development" && (
            <>
              <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="bottom-right"
                position="bottom"
              />
              <WebSocketDebugger />
            </>
          )}

          <Toaster position="top-right" />
        </QueryClientProvider>
      </div>
    </div>
  );
};

export default App;
